import { designTokens } from "@itandi/itandi-bb-ui";
import { Alertv2 } from "@itandi/itandi-bb-ui/Alertv2";
import { Buttonv2 } from "@itandi/itandi-bb-ui/Buttonv2";
import { Dropdown } from "@itandi/itandi-bb-ui/Dropdown";
import { DropdownMenu } from "@itandi/itandi-bb-ui/DropdownMenu";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { Paragraph } from "@itandi/itandi-bb-ui/Paragraph";
import { Sortable } from "@itandi/itandi-bb-ui/Sortable";
import { Table } from "@itandi/itandi-bb-ui/Table";
import { Tbody } from "@itandi/itandi-bb-ui/Tbody";
import { Td } from "@itandi/itandi-bb-ui/Td";
import { Th } from "@itandi/itandi-bb-ui/Th";
import { Thead } from "@itandi/itandi-bb-ui/Thead";
import { Tr } from "@itandi/itandi-bb-ui/Tr";
import { useCallback, useState } from "react";
import useSWRMutation from "swr/mutation";
import { renderDropDownButton } from "~/components/common/parts/dropdown/DropDownButton";
import { VendorUserModal } from "~/components/specifics/vendors/settings/vendor_users/VendorUserModal";
import { useIsItandi } from "~/context/VendorSession";
import { useVendorUsers } from "~/hooks/vendors/useVendorUsers";
import type { VendorUser } from "~/models/VendorUser";
import { isSuccess } from "~/utils/api/api_handler";
import { destroy, updateSeq } from "~/utils/api/vendors/VendorUser";
import { generatePath } from "~/utils/generatePath";

type SortableItems = { data: VendorUser; id: string }[];

export function VendorUserList() {
	const { notify } = useNotification();
	const canEditSlackMemberId = useIsItandi();
	const { data: vendorUsers } = useVendorUsers().index();
	const [editingVendorUser, setEditingVendorUser] = useState<VendorUser | null>(
		null
	);
	const [deletingVendorUser, setDeletingVendorUser] =
		useState<VendorUser | null>(null);

	const { trigger: seqTrigger } = useSWRMutation(
		generatePath("/vendors/vendor_users"),
		async (_key, { arg }: { arg: { sortedItems: VendorUser[] } }) =>
			updateSeq(arg.sortedItems)
	);

	const { trigger: destroyTrigger, isMutating: isDeleting } = useSWRMutation(
		generatePath("/vendors/vendor_users"),
		async (_key, { arg }: { arg: { vendorUserId: number } }) =>
			destroy(arg.vendorUserId)
	);

	const sortableItems: SortableItems = (vendorUsers ?? []).map(
		(vendorUser) => ({
			data: vendorUser,
			id: String(vendorUser.id)
		})
	);

	const onSortEnd = useCallback(
		async (newItems: SortableItems) => {
			const sortedItems: VendorUser[] = newItems.map((item, index) => ({
				...item.data,
				seq: index + 1
			}));
			const result = await seqTrigger(
				{ sortedItems },
				{ optimisticData: sortedItems, rollbackOnError: true }
			);
			if (!isSuccess(result)) {
				notify({ content: "並び順の更新に失敗しました。", variant: "error" });
			}
		},
		[notify, seqTrigger]
	);

	const handleDelete = async (): Promise<void> => {
		const result = await destroyTrigger({
			vendorUserId: deletingVendorUser!.id
		});
		if (isSuccess(result)) {
			notify({ content: "削除しました。", variant: "success" });
			setDeletingVendorUser(null);
		} else {
			notify({ content: "エラーが発生しました。", variant: "error" });
		}
	};

	return (
		<>
			<Table>
				<Thead>
					<Tr>
						<Th width="3rem">並替</Th>
						<Th>担当者名</Th>
						<Th>メールアドレス</Th>
						<Th>電話番号</Th>
						{canEditSlackMemberId ? <Th>Slack ID</Th> : null}
						<Th width="10rem">操作</Th>
					</Tr>
				</Thead>
				<Tbody>
					<Sortable
						items={sortableItems}
						onSortEnd={onSortEnd}
						variant="vertical"
					>
						{({ dragHandle, item }) => (
							<Tr key={item.id}>
								<Td>
									<Flex justifyContent="center">{dragHandle}</Flex>
								</Td>
								<Td>{item.data.name}</Td>
								<Td>{item.data.email}</Td>
								<Td>{item.data.tel}</Td>
								{canEditSlackMemberId ? (
									<Td>{item.data.slackMemberId ?? "未設定"}</Td>
								) : null}
								<Td>
									<Dropdown button={renderDropDownButton("操作")}>
										<DropdownMenu
											onClick={() => {
												setEditingVendorUser(item.data);
											}}
										>
											編集
										</DropdownMenu>
										<DropdownMenu
											danger
											onClick={() => {
												setDeletingVendorUser(item.data);
											}}
										>
											削除
										</DropdownMenu>
									</Dropdown>
								</Td>
							</Tr>
						)}
					</Sortable>
				</Tbody>
			</Table>
			{editingVendorUser ? (
				<VendorUserModal
					onClose={() => {
						setEditingVendorUser(null);
					}}
					vendorUser={editingVendorUser}
				/>
			) : null}
			{deletingVendorUser ? (
				<Modalv2
					isOpen
					onClose={() => {
						setDeletingVendorUser(null);
					}}
					size="s"
				>
					<ModalHeader title="担当者の削除" />
					<ModalBody>
						<Paragraph>「{deletingVendorUser.name}」を削除します。</Paragraph>
						<Alertv2 mt={designTokens["spacing/m"]} variant="warning">
							<Paragraph>この操作は元に戻せません。</Paragraph>
						</Alertv2>
					</ModalBody>
					<ModalFooter
						leftArea={
							<Buttonv2
								disabled={isDeleting}
								onClick={() => {
									setDeletingVendorUser(null);
								}}
								variant="secondary"
							>
								キャンセル
							</Buttonv2>
						}
						rightArea={
							<Buttonv2
								color="danger"
								loading={isDeleting}
								onClick={handleDelete}
								variant="primary"
							>
								削除
							</Buttonv2>
						}
					/>
				</Modalv2>
			) : null}
		</>
	);
}
